import React, { createContext, useContext, useState, useCallback } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAsaPersonality } from '@/hooks/useAsaPersonality';
import { generateContentWithFallback } from '@/lib/geminiClient';
import { useSession } from '@/lib/SessionContext';
import { Loader2 } from 'lucide-react';

const AsaConfirmContext = createContext(null);

export function AsaConfirmProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [resolveCallback, setResolveCallback] = useState(null);
  const [aiMessage, setAiMessage] = useState('');
  const [isAiLoading, setIsAiLoading] = useState(false);
  
  const { sapaan, gaya, isFadli } = useAsaPersonality();
  const { member } = useSession();

  const confirm = useCallback((text) => {
    setMessage(text);
    setAiMessage(''); // Reset before fetching
    setIsOpen(true);
    
    // Trigger AI generation
    setIsAiLoading(true);
    generateAiMessage(text);
    
    return new Promise((resolve) => {
      setResolveCallback(() => resolve);
    });
  }, [member, sapaan, gaya, isFadli]);

  const handleConfirm = () => {
    setIsOpen(false);
    if (resolveCallback) resolveCallback(true);
  };

  const handleCancel = () => {
    setIsOpen(false);
    if (resolveCallback) resolveCallback(false);
  };

  const getAsaMessageFallback = (originalText) => {
    const isDelete = originalText.toLowerCase().includes('hapus') || originalText.toLowerCase().includes('delete');
    
    if (isFadli) {
      if (isDelete) return `Fadlii, yakin nih mau hapus data ini? Hati-hati lho kalau salah hapus sayang hehe~ 🥺`;
      return `Fadli, beneran mau lanjutin proses ini? ${originalText}`;
    }

    if (gaya === 'santai') {
      if (isDelete) return `Halo ${sapaan}, yakin nih mau hapus data ini? Datanya bakal ilang lho!`;
      return `Eh ${sapaan}, beneran mau ${originalText.toLowerCase()}?`;
    }

    if (gaya === 'formal') {
      if (isDelete) return `Mohon maaf ${sapaan}, apakah Anda yakin ingin menghapus data ini? Tindakan ini tidak dapat dibatalkan.`;
      return `Mohon konfirmasi ${sapaan}, ${originalText}`;
    }

    // Default
    if (isDelete) return `Halo ${sapaan}, Anda yakin ingin menghapus data ini?`;
    return `Halo ${sapaan}, ${originalText}`;
  };

  const generateAiMessage = async (originalText) => {
    if (!member) {
      setAiMessage(getAsaMessageFallback(originalText));
      setIsAiLoading(false);
      return;
    }
    try {
      const prompt = `Kamu adalah ASA, asisten AI cerdas di Satala Dermatech. Aku (user yang login) bernama ${sapaan}.
      Info sifatku/kepribadianku: ${member.asa_personality || (isFadli ? 'Aku Fadli si bestie developer' : 'Santai dan asik.')}
      Tugasmu: Sampaikan pesan konfirmasi ini kepadaku: "${originalText}"
      Gunakan gaya bahasa yang SANGAT SESUAI dengan kepribadianku di atas. 
      Jika ini peringatan hapus data, beri nada hati-hati tapi tetap dengan gaya bahasaku. 
      Maksimal 2 kalimat saja. Tanpa tanda kutip di awal/akhir.`;
      
      const response = await generateContentWithFallback("Kamu adalah asisten ASA.", prompt, [], "gemini-3.5-flash");
      setAiMessage(response.trim());
    } catch (error) {
      console.error("Gagal generate pesan konfirmasi AI:", error);
      // Fallback manual jika 5 API Key error semua!
      setAiMessage(getAsaMessageFallback(originalText));
    } finally {
      setIsAiLoading(false);
    }
  };

  return (
    <AsaConfirmContext.Provider value={{ confirm }}>
      {children}
      
      <Dialog open={isOpen} onOpenChange={(open) => {
        if (!open) handleCancel();
      }}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden border-[#E6D5B8] bg-white/95 shadow-2xl rounded-2xl max-h-[90vh] flex flex-col my-auto">
          <div className="p-5 sm:p-6 overflow-y-auto flex-1 overscroll-contain">
            
            {/* Header & Avatar */}
            <div className="flex flex-col items-center justify-center text-center mb-5 shrink-0">
              <div className="relative mb-3">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full overflow-hidden border-4 border-white shadow-lg relative z-10 bg-slate-100">
                  <img src="/asa-profile.png" alt="ASA" className="w-full h-full object-cover" onError={(e) => { e.target.src = 'https://ui-avatars.com/api/?name=Asa&background=0D8ABC&color=fff'; }} />
                </div>
                {/* Glow Effect behind avatar */}
                <div className="absolute inset-0 bg-[#8C6208] rounded-full blur-xl opacity-30 scale-150 animate-pulse"></div>
                {/* Online Indicator */}
                <div className="absolute bottom-1 right-1 w-4 h-4 bg-emerald-500 border-2 border-white rounded-full z-20"></div>
              </div>
              <h3 className="text-lg font-black text-[#302621]">ASA AI</h3>
              <p className="text-[11px] text-emerald-600 font-bold tracking-widest uppercase">Online • Asisten Anda</p>
            </div>

            {/* Message Bubble */}
            <div className="bg-[#FFFDF9] border border-[#E6D5B8]/50 p-4 rounded-xl shadow-inner mb-4 relative min-h-[80px] flex items-center justify-center">
              <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-[#FFFDF9] border-t border-l border-[#E6D5B8]/50 rotate-45"></div>
              {isAiLoading ? (
                <div className="flex items-center gap-2 text-[#7A695B] font-medium text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" /> ASA sedang memproses...
                </div>
              ) : (
                <p className="text-sm text-[#302621] text-center font-medium leading-relaxed relative z-10 w-full">
                  "{aiMessage}"
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button 
                variant="outline" 
                onClick={handleCancel}
                className="flex-1 rounded-xl h-11 font-bold text-[#7A695B] border-[#E6D5B8] hover:bg-slate-50"
              >
                Batal Saja
              </Button>
              <Button 
                onClick={handleConfirm}
                className="flex-1 rounded-xl h-11 font-bold bg-gradient-to-r from-[#8C6208] to-[#D4AF37] hover:from-[#6A4A06] hover:to-[#B38D19] text-white shadow-lg shadow-[#8C6208]/20 border-0"
              >
                Ya, Lanjutkan
              </Button>
            </div>
            
          </div>
        </DialogContent>
      </Dialog>
    </AsaConfirmContext.Provider>
  );
}

export function useAsaConfirm() {
  const context = useContext(AsaConfirmContext);
  if (!context) {
    throw new Error('useAsaConfirm must be used within an AsaConfirmProvider');
  }
  return context;
}
